import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiPhone,
  FiCalendar, FiLogOut, FiEdit3, FiSave, FiX, FiLogIn,
  FiGithub, FiCheck, FiArrowUpRight, FiShield, FiArrowLeft,
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { db } from "./firebase";
import {
  collection, getDocs, addDoc, query, where, updateDoc, doc,
} from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import Navbar from "./Navbar";
import {
  getAuth, GoogleAuthProvider, signInWithPopup,
  updateProfile, GithubAuthProvider, updatePassword,
} from "firebase/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Hero.css";   // design system: tokens, .kk-btn, .kk-eyebrow
import "./Login.css";  // everything unique to the auth page

/* ═══════════════ HELPERS ═══════════════ */

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "U";

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

/* ═══════════════ PARTICLES ═══════════════ */

function LoginParticles() {
  const parts = useRef(
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="lg-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="lg-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.06, 0.34, 0.06] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* ═══════════════ FIELD ═══════════════ */

function Field({ id, label, icon, children, hint }) {
  return (
    <div className="lg-field">
      <label className="lg-field__label" htmlFor={id}>
        {icon}
        {label}
      </label>
      <div className="lg-input">{children}</div>
      {hint && <span className="lg-field__hint">{hint}</span>}
    </div>
  );
}

/* ═══════════════ LEFT POSTER (pure CSS — no network) ═══════════════ */

function PosterPanel() {
  return (
    <aside className="lg-poster" aria-hidden="true">
      <div className="lg-poster__bg">
        <span className="lg-poster__grid" data-plx="0.05" />
        <span className="lg-poster__glow" data-plx="0.16" />
        <span className="lg-poster__word" data-plx="0.09">LOGIN</span>
      </div>

      <div className="lg-poster__in">
        <span className="lg-poster__eyebrow"><FiShield /> KKS·1436 — secure access</span>

        <p className="lg-poster__title">
          Better <span className="kk-serif">code.</span>
          <br />
          Bigger <span className="kk-serif kk-acid">dreams.</span>
        </p>

        <p className="lg-poster__desc">
          One account, every page — projects, notes, code and the admin console.
        </p>

        <ul className="lg-poster__list">
          <li><FiCheck /> Firebase authentication</li>
          <li><FiCheck /> Google &amp; GitHub sign-in</li>
          <li><FiCheck /> Session kept in your browser</li>
        </ul>

        <div className="lg-chips">
          <span className="lg-chip lg-chip--1">{"</>"}</span>
          <span className="lg-chip lg-chip--2">{"{ }"}</span>
          <span className="lg-chip lg-chip--3">{"=>"}</span>
        </div>
      </div>
    </aside>
  );
}

/* ═══════════════ LOGIN FORM ═══════════════ */

function LoginForm({
  email, setEmail, password, setPassword,
  showPassword, setShowPassword, onSubmit,
  onGoogleLogin, onGithubLogin,
}) {
  const [remember, setRemember] = useState(() => {
    try { return !!localStorage.getItem("remember-email"); } catch { return false; }
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem("remember-email");
      if (saved && !email) setEmail(saved);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    try {
      if (remember && email) localStorage.setItem("remember-email", email);
      else localStorage.removeItem("remember-email");
    } catch { /* ignore */ }
    onSubmit(e);
  };

  return (
    <motion.div
      className="lg-split"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <PosterPanel />

      <div className="lg-form-side">
        <div className="lg-head">
          <span className="kk-eyebrow">Account / Sign in</span>
          <h1 className="lg-h1">
            Welcome <span className="kk-serif kk-acid">back.</span>
          </h1>
          <p className="lg-sub">Log in to pick up where you left off.</p>
        </div>

        <form onSubmit={submit} className="lg-form">
          <Field id="lg-email" label="Email address" icon={<FiMail />}>
            <FiMail className="lg-input__ic" />
            <input
              id="lg-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>

          <Field id="lg-pass" label="Password" icon={<FiLock />}>
            <FiLock className="lg-input__ic" />
            <input
              id="lg-pass"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="lg-eye"
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? "Hide password" : "Show password"}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </Field>

          <div className="lg-row">
            <label className="lg-remember">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>Remember me</span>
            </label>
            <button
              type="button"
              className="lg-forgot"
              onClick={() => toast.info("Please contact support to reset your password.")}
            >
              Forgot password?
            </button>
          </div>

          <motion.button
            type="submit"
            className="kk-btn lg-btn"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.985 }}
          >
            Login <FiLogIn />
          </motion.button>
        </form>

        <div className="lg-or"><span>or continue with</span></div>

        <div className="lg-social">
          <motion.button
            className="lg-soc"
            onClick={onGoogleLogin}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.97 }}
            title="Continue with Google"
            aria-label="Continue with Google"
          >
            <FcGoogle />
            <span>Google</span>
          </motion.button>

          <motion.button
            className="lg-soc"
            onClick={onGithubLogin}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.97 }}
            title="Continue with GitHub"
            aria-label="Continue with GitHub"
          >
            <FiGithub />
            <span>GitHub</span>
          </motion.button>
        </div>

        <p className="lg-signup">
          Don&rsquo;t have an account? <Link to="/register">Sign up</Link>
        </p>

        <p className="lg-secure"><FiLock /> Secure login — your data stays in your project.</p>
      </div>
    </motion.div>
  );
}

/* ═══════════════ PROFILE CARD ═══════════════ */

function ProfileCard({ user, isEditing, onEdit, onSave, onCancel, onLogout, editData, setEditData }) {
  const [broken, setBroken] = useState(false);

  return (
    <motion.div
      className="lg-profile"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="lg-prof__head">
        <div className="lg-avatar-wrap">
          {user.PhotoURL && !broken ? (
            <img
              src={user.PhotoURL}
              alt="Profile"
              className="lg-avatar"
              onError={() => setBroken(true)}
            />
          ) : (
            <span className="lg-avatar lg-avatar--mono">{initials(user.Name)}</span>
          )}
          <span className="lg-avatar-badge"><FiCheck /></span>
        </div>

        <div className="lg-prof__id">
          <span className="kk-eyebrow">Account / Profile</span>
          <h1 className="lg-h1 lg-h1--sm">
            Welcome, <span className="kk-serif kk-acid">{user.Name}</span>
          </h1>
          <p className="lg-prof__email">{user.Email}</p>
        </div>

        <span className="lg-prof__tag"><FiShield /> Active session</span>
      </div>

      <AnimatePresence mode="wait">
        {isEditing ? (
          <motion.div
            key="edit"
            className="lg-edit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
          >
            <div className="lg-edit__grid">
              <Field id="lg-name" label="Name" icon={<FiUser />}>
                <FiUser className="lg-input__ic" />
                <input
                  id="lg-name"
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  placeholder="Your name"
                />
              </Field>

              <Field id="lg-contact" label="Contact" icon={<FiPhone />}>
                <FiPhone className="lg-input__ic" />
                <input
                  id="lg-contact"
                  type="text"
                  value={editData.contact}
                  onChange={(e) => setEditData({ ...editData, contact: e.target.value })}
                  placeholder="Phone number"
                />
              </Field>

              <div className="lg-field">
                <label className="lg-field__label" htmlFor="lg-gender">Gender</label>
                <div className="lg-input lg-input--select">
                  <select
                    id="lg-gender"
                    value={editData.gender}
                    onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <Field id="lg-dob" label="Date of birth" icon={<FiCalendar />}>
                <FiCalendar className="lg-input__ic" />
                <input
                  id="lg-dob"
                  type="date"
                  value={editData.dob}
                  onChange={(e) => setEditData({ ...editData, dob: e.target.value })}
                />
              </Field>

              <div className="lg-edit__wide">
                <Field id="lg-newpass" label="New password" icon={<FiLock />}>
                  <FiLock className="lg-input__ic" />
                  <input
                    id="lg-newpass"
                    type="password"
                    value={editData.password}
                    onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                    placeholder="New password"
                    autoComplete="new-password"
                  />
                </Field>
              </div>
            </div>

            <div className="lg-actions">
              <motion.button
                className="kk-btn lg-btn-auto"
                onClick={onSave}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.985 }}
              >
                <FiSave /> Save changes
              </motion.button>
              <motion.button
                className="kk-btn kk-btn--ghost lg-btn-auto"
                onClick={onCancel}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.985 }}
              >
                <FiX /> Cancel
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="view"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
          >
            <dl className="lg-details">
              <div className="lg-details__row">
                <dt><FiMail /> Email</dt>
                <dd>{user.Email}</dd>
              </div>
              <div className="lg-details__row">
                <dt><FiPhone /> Contact</dt>
                <dd>{user.Contact || "—"}</dd>
              </div>
              <div className="lg-details__row">
                <dt><FiUser /> Gender</dt>
                <dd>{user.Gender || "—"}</dd>
              </div>
              <div className="lg-details__row">
                <dt><FiCalendar /> Date of birth</dt>
                <dd>{user.Dateofbirth || "—"}</dd>
              </div>
            </dl>

            <div className="lg-actions">
              <motion.button
                className="kk-btn lg-btn-auto"
                onClick={onEdit}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.985 }}
              >
                <FiEdit3 /> Edit profile
              </motion.button>
              <motion.button
                className="kk-btn kk-btn--ghost lg-btn-auto lg-btn-danger"
                onClick={onLogout}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.985 }}
              >
                <FiLogOut /> Logout
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═══════════════ MAIN LOGIN COMPONENT ═══════════════ */

// LOGIC: identical to your original — same state, same handlers, same
// Firebase/Auth/EmailJS calls. Only styling/classes + preview guards changed.
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: "", contact: "", gender: "", dob: "", password: "",
  });

  const navigate = useNavigate();
  const rootRef = useRef(null);
  useParallax(rootRef);

  // Preview-safe: getAuth() throws when no Firebase app is initialized.
  let auth = null;
  try { auth = getAuth(); } catch { auth = null; }
  const adminEmail = "karanksxxx@gmail.com";

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    if (loggedInUser) {
      setUser(loggedInUser);
      if (loggedInUser.Email === adminEmail) {
        navigate("/admin/logins");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const sanitizeInput = (input) => input.replace(/[<>{}]/g, "");

  // Google Login
  async function handleGoogleLogin() {
    if (!auth || !db) { toast.info("Login is disabled in preview mode."); return; }
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const googleUser = result.user;
      const userProfilePic = googleUser.photoURL || "";

      const q = query(collection(db, "Register"), where("Email", "==", googleUser.email));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        await addDoc(collection(db, "Register"), {
          Name: googleUser.displayName || "No Name",
          Email: googleUser.email,
          Contact: "Not Provided",
          Gender: "Not Provided",
          Password: "GoogleUser",
          Dateofbirth: "Not Provided",
          Address: "Not Provided",
          PhotoURL: userProfilePic,
        });
      } else {
        await updateDoc(doc(db, "Register", snapshot.docs[0].id), { PhotoURL: userProfilePic });
      }

      await addDoc(collection(db, "Login"), {
        Name: googleUser.displayName,
        Email: googleUser.email,
        LoginTime: new Date(),
        PhotoURL: userProfilePic,
      });

      const userData = { Name: googleUser.displayName, Email: googleUser.email, PhotoURL: userProfilePic };
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      if (googleUser.email === adminEmail) navigate("/admin/logins");
      else toast.success("Google Login Successful! 🎉");

      sendLoginEmail(googleUser.displayName, googleUser.email);
    } catch (error) {
      console.error("Google Login Error:", error);
      toast.error("Google login failed.");
    }
  }

  // GitHub Login
  async function handleGithubLogin() {
    if (!auth || !db) { toast.info("Login is disabled in preview mode."); return; }
    const provider = new GithubAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const githubUser = result.user;
      const userProfilePic = githubUser.photoURL || "";

      const q = query(collection(db, "Register"), where("Email", "==", githubUser.email));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        await addDoc(collection(db, "Register"), {
          Name: githubUser.displayName || "GitHub User",
          Email: githubUser.email,
          Contact: "Not Provided",
          Gender: "Not Provided",
          Password: "GithubUser",
          Dateofbirth: "Not Provided",
          Address: "Not Provided",
          PhotoURL: userProfilePic,
        });
      } else {
        await updateDoc(doc(db, "Register", snapshot.docs[0].id), { PhotoURL: userProfilePic });
      }

      await addDoc(collection(db, "Login"), {
        Name: githubUser.displayName,
        Email: githubUser.email,
        LoginTime: new Date(),
        PhotoURL: userProfilePic,
      });

      const userData = { Name: githubUser.displayName, Email: githubUser.email, PhotoURL: userProfilePic };
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      if (githubUser.email === adminEmail) navigate("/admin/logins");
      else toast.success("GitHub Login Successful! 🐙");

      sendLoginEmail(githubUser.displayName, githubUser.email);
    } catch (error) {
      console.error("GitHub Login Error:", error);
      toast.error("GitHub login failed.");
    }
  }

  // Email/Password Login
  async function handleSubmit(e) {
    e.preventDefault();
    if (!db) { toast.info("Login is disabled in preview mode."); return; }
    const cleanEmail = sanitizeInput(email);
    const cleanPassword = sanitizeInput(password);

    try {
      const querySnapshot = await getDocs(collection(db, "Register"));
      let userFound = null;

      for (const docSnap of querySnapshot.docs) {
        const userData = docSnap.data();
        if (userData.Email === cleanEmail && userData.Password === cleanPassword) {
          userFound = { ...userData, PhotoURL: userData.PhotoURL || "" };

          await addDoc(collection(db, "Login"), {
            Name: userFound.Name,
            Email: userFound.Email,
            LoginTime: new Date(),
            PhotoURL: userFound.PhotoURL,
          });

          localStorage.setItem("user", JSON.stringify(userFound));
          setUser(userFound);
          sendLoginEmail(userFound.Name, userFound.Email);

          if (userFound.Email === adminEmail) navigate("/admin/logins");
          else toast.success("Login Successful! 🎉");
          break;
        }
      }

      if (!userFound) toast.error("Invalid Email or Password");
    } catch (err) {
      console.error("Login Error:", err);
      toast.error("Login failed. Please try again.");
    }
  }

  // Profile Update
  async function handleProfileUpdate() {
    if (!editData.name.trim() || !editData.password.trim()) {
      toast.error("Name and Password required");
      return;
    }

    try {
      const q = query(collection(db, "Register"), where("Email", "==", user.Email));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        await updateDoc(doc(db, "Register", snapshot.docs[0].id), {
          Name: editData.name,
          Contact: editData.contact,
          Gender: editData.gender,
          Dateofbirth: editData.dob,
          Password: editData.password,
        });

        if (auth.currentUser) {
          await updateProfile(auth.currentUser, { displayName: editData.name });
          if (editData.password !== user.Password) {
            await updatePassword(auth.currentUser, editData.password);
          }
        }

        const updatedUser = {
          ...user,
          Name: editData.name,
          Contact: editData.contact,
          Gender: editData.gender,
          Dateofbirth: editData.dob,
          Password: editData.password,
        };

        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setIsEditing(false);
        toast.success("Profile updated successfully! ✅");
      }
    } catch (error) {
      console.error("Update Error:", error);
      toast.error("Failed to update profile.");
    }
  }

  // Email notification
  function sendLoginEmail(name, email) {
    if (!db) return;
    emailjs.send(
      "service_31euj04",
      "template_138qw9g",
      {
        user_name: name,
        to_email: email,
        login_time: new Date().toLocaleString(),
        message: `Hi ${name}, you have logged in successfully at ${new Date().toLocaleString()}.`,
      },
      "gQJJ82qqno2_M07FO"
    ).catch((err) => console.error("Email error:", err));
  }

  // Logout
  function logout() {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  }

  // Edit handler
  function startEditing() {
    setEditData({
      name: user.Name || "",
      contact: user.Contact || "",
      gender: user.Gender || "",
      dob: user.Dateofbirth || "",
      password: user.Password || "",
    });
    setIsEditing(true);
  }

  return (
    <div className="kk-root lg-page" ref={rootRef}>
      <LoginParticles />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        theme="dark"
        toastStyle={{
          background: "#0b0b10",
          border: "1px solid rgba(200,255,61,.28)",
          color: "#f3f3f5",
        }}
      />

      {/* ══════ BRAND BAR ══════ */}
      <header className="lg-bar">
        <div className="kk-wrap lg-bar__in">
          <Link to="/" className="lg-brand">KKS<span>·</span>1436</Link>
          <Link to="/" className="lg-back"><FiArrowLeft /> Back to site</Link>
        </div>
      </header>

      {/* ══════ MAIN ══════ */}
      <main className="lg-main">
        <div className="kk-wrap">
          {!user ? (
            <LoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              onSubmit={handleSubmit}
              onGoogleLogin={handleGoogleLogin}
              onGithubLogin={handleGithubLogin}
            />
          ) : (
            <ProfileCard
              user={user}
              isEditing={isEditing}
              onEdit={startEditing}
              onSave={handleProfileUpdate}
              onCancel={() => setIsEditing(false)}
              onLogout={logout}
              editData={editData}
              setEditData={setEditData}
            />
          )}
        </div>
      </main>

      {/* ══════ STRIP ══════ */}
      <footer className="lg-foot">
        <div className="kk-wrap lg-foot__in">
          <span>© {new Date().getFullYear()} Karan Kumar — KKS·1436</span>
          <Link to="/contact" className="lg-foot__link">Need help? <FiArrowUpRight /></Link>
        </div>
      </footer>

      {/* <Navbar /> */}
    </div>
  );
}
